// ...existing imports...
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { SelectItem } from 'primeng/api';
import { NotificationTypes } from 'src/app/app.enums';
import { ArtistProduct } from 'src/app/models/artistproduct.model';
import { SetupItems } from 'src/app/models/setupItems';
import { ArtTypeService } from 'src/app/Services/art-type.service';
import { ArtistsService } from 'src/app/Services/artists.service';
import { ApplicationUser, AuthConfig, AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { ClayService } from 'src/app/Services/clay.service';
import { GlazesService } from 'src/app/Services/glazes.service';
import { SeriesService } from 'src/app/Services/series.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { ShareService } from 'src/app/Services/shared/share.service';
import { UsersService } from 'src/app/Services/users.service';
import { TranslocoService } from '@jsverse/transloco';
import { OnboardingStepsEnum } from '../../auth/auth.component';

// Interface for menu items in the onboarding checklist
export interface MenuItem {
  id: string;
  label: string;
}

@Component({
    selector: 'app-artist-onboarding-checklist',
    templateUrl: './artist-onboarding-checklist.component.html',
    styleUrls: ['./artist-onboarding-checklist.component.css'],
    standalone: false
})
export class ArtistOnboardingCheckListComponent implements OnInit, OnDestroy {
  // User and product data
  public updatedUser!: ApplicationUser;
  public createproduct!: ArtistProduct;

  // Art type and material selections
  public artTypes: SetupItems[] = [];
  public clay: SetupItems[] = [];
  public selectedClays: SetupItems = {} as SetupItems;
  public authConfig: AuthConfig = {};
  public artistId!: string;
  public artists: SelectItem[] = [];
  public series: SetupItems[] = [];
  public selectedSeries: SetupItems = {} as SetupItems;
  public glazes: SetupItems[] = [];
  public selectedGlaze: SetupItems = {} as SetupItems;

  // Onboarding progress tracking
  public currentIndex = 0;
  public completedSteps: string[] = [];

  // Authentication and user status
  public isAuthenticated: boolean = false;
  public isArtist: boolean = false;
  public artistArtTypeId: string[] = [];
  public selectedArtTypesDropdownItems: SelectItem[] = [];
  public selectedArtTypeId: string = '';
  public userLogoUrl!: string;

  // Subscription plan related
  public numberOfArtTypeSelection!: number;
  public upgradeDialogVisible = false;
  public isFreePlanUser: boolean = true;
  public hasFreeTrailEnded: boolean | undefined;
  public isOnFreeTrial!: boolean;

  // UI state
  public selectedArtType: string = '';
  public primaryArtType: SelectItem = { label: '', value: '' };
  public showSidebar!: boolean;
  public HideUserOnBoardingCheckList!: boolean;
  public onboardingSteps: any[] = [];
  public errorMessage: string = '';

  // Art type IDs for different sections
  public selectedSeriesArtTypeId: string = '';
  public selectedMaterialArtTypeId: string = '';
  public selectedFeatureArtTypeId: string = '';

  // Flag to track if art types have been loaded
  private artTypesLoaded = false;

  // Onboarding steps menu
  menuList: MenuItem[] = [
    { id: OnboardingStepsEnum[OnboardingStepsEnum.primaryArtType], label: 'Select your main artwork type' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.additionalTypes], label: 'Add other artwork types' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.updateUserDetails], label: 'Update your details' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.artistStatementResume], label: 'Add an artist statement & resume' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.setupArtworkSeries], label: 'Set up your artwork series' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.preferredMaterials], label: 'Set up your preferred materials' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.marketingFeatures], label: 'Set up your marketing features' },
    { id: OnboardingStepsEnum[OnboardingStepsEnum.addFirstArtwork], label: 'Add your first artwork to ArtFlow Pro' }
  ];

  constructor(
    private router: Router,
    private artTypeService: ArtTypeService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private UsersService: UsersService,
    private clayService: ClayService,
    private authService: AuthService,
    private seriesService: SeriesService,
    private glazesService: GlazesService,
    private artistService: ArtistsService,
    private shareService: ShareService,
    private transloco: TranslocoService,
  ) { }

  ngOnInit(): void {
    // Subscribe to sidebar visibility changes
        this.shareService.updateVisibility(false);


    // Initialize user and authentication data
    this.artistId = this.authService.getUserId();
    this.authConfig = this.authService.getAuthConfig();
    this.isAuthenticated = this.authService.isAuthenticated();

    // Initialize subscription plan data
    this.hasFreeTrailEnded = this.authService.hasFreeTrialEnded();
    this.isOnFreeTrial = this.authService.isOnFreetrial();
    this.isFreePlanUser = this.authService.isFreePlanUser();
    this.numberOfArtTypeSelection = this.authService.getArtTypeSelection();

    // Load initial data
    this.getPrimaryArtType();
    this.HideUserOnBoardingCheckList = this.authService.HideUserOnBoardingCheckList();
    this.loadOnboardingSteps();
    this.currentIndex = 0;
    this.updateActiveClass();
  }
   @HostListener('window:resize')
    onResize() {
      if (this.artistId) {  // Only execute for artist profiles
        this.checkScreenSize();
      }
    }
     private checkScreenSize() {
      this.shareService.updateVisibility(false);
  }

  ngOnDestroy(): void {
    // Reset sidebar visibility when component is destroyed
    this.shareService.updateVisibility(true);
  }

  /**
   * Loads the user's onboarding steps from the server
   */
  loadOnboardingSteps(): void {
    this.UsersService.fetchUserOnboardingSteps().subscribe({
      next: (response) => {
        this.onboardingSteps = response || [];
        this.completedSteps = Array.from(new Set(this.onboardingSteps.map((step: any) => step.OnboardingStepId)));
        const firstIncomplete = this.menuList.findIndex(item => !this.completedSteps.includes(item.id));
        this.currentIndex = firstIncomplete !== -1 ? firstIncomplete : 0;
        this.updateActiveClass();
      },
      error: () => {
        this.errorMessage = 'Error fetching onboarding steps';
      }
    });
  }

  /**
   * Updates a specific onboarding step on the server
   * @param onboardingStepId The ID of the step to update
   */
  updateOnboardingStep(onboardingStepId: string): void {
    this.UsersService.updateUserOnboardingStep(onboardingStepId).subscribe({
      next: (response) => {
        if (response.Success) {
          if (!this.completedSteps.includes(onboardingStepId)) {
            this.completedSteps.push(onboardingStepId);
          }
        } else {
          this.errorMessage = response.Errors?.join(', ') || 'Update failed';
        }
      },
      error: () => {
        this.errorMessage = 'HTTP Error updating onboarding step';
      }
    });
  }

  /**
   * Updates the active and completed classes for menu items
   */
  updateActiveClass(): void {
    this.menuList.forEach((item, index) => {
      const element = document.getElementById(item.id);
      if (element) {
        element.classList.remove('active');
        element.classList.remove('completed');
        if (index === this.currentIndex) {
          element.classList.add('active');
        }
        if (this.completedSteps.includes(item.id)) {
          element.classList.add('completed');
        }
      }
    });
  }

  /**
   * Advances to the next onboarding step
   */
  advance(): void {
    const currentItem = this.menuList[this.currentIndex].id;
    if (!this.completedSteps.includes(currentItem)) {
      this.updateOnboardingStep(currentItem);
    }
    if (this.currentIndex < this.menuList.length - 1) {
      this.currentIndex++;
      this.updateActiveClass();
    }
  }

  /**
   * Navigates to a specific onboarding step
   * @param route The ID of the step to navigate to
   */
  navigateTo(route: string): void {
    this.currentIndex = this.menuList.findIndex(item => item.id === route);
    this.updateActiveClass();
  }

  /**
   * Saves the current step data and advances to the next step
   */
  public saveAndAdvance(): void {
    this.ngxSpinnerService.show();
    const currentItem = this.menuList[this.currentIndex].id;
    this.UsersService.updateUserOnboardingStep(currentItem).subscribe({
      next: (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          switch (currentItem) {
            case 'primaryArtType':
              this.savePrimaryArtType();
              break;
            case 'additionalTypes':
              this.saveArtType();
              break;
            case 'updateUserDetails':
              this.saveUpdatedUser(this.updatedUser);
              break;
            case 'artistStatementResume':
              this.saveUpdatedUserResume(this.updatedUser);
              break;
            case 'addFirstArtwork':
              this.saveArtistProduct(this.createproduct);
              break;
            default:
              this.advance();
              break;
          }
        } else {
          this.errorMessage = response.Errors?.join(', ') || 'Update failed';
        }
      },
      error: () => {
        this.ngxSpinnerService.hide();
        this.errorMessage = 'HTTP Error updating onboarding step';
      }
    });
  }

  /**
   * Determines if a menu item is clickable
   * @param index The index of the menu item
   * @returns True if the item is clickable
   */
  isItemClickable(index: number): boolean {
    return true;
  }

  /**
   * Handles checkbox changes for art type selection
   * @param event The checkbox change event
   * @param artistArtTypeId The ID of the art type being toggled
   */
  onCheckboxChange(event: Event, artistArtTypeId: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      if (artistArtTypeId && !this.artistArtTypeId.includes(artistArtTypeId)) {
        // Check subscription limits for free users
        if (this.isFreePlanUser && (this.hasFreeTrailEnded || !this.isOnFreeTrial) && this.artistArtTypeId.length >= this.numberOfArtTypeSelection) {
          (event.target as HTMLInputElement).checked = false;
          this.upgradeDialogVisible = true;
        } else {
          this.artistArtTypeId.push(artistArtTypeId);
          this.artistArtTypeId = this.artistArtTypeId.filter(id => id != null);
        }
      }
    } else {
      this.artistArtTypeId = this.artistArtTypeId.filter(id => id !== artistArtTypeId && id != null);
    }
  }

  /**
   * Updates all art type related state when the art type changes
   * @param artTypeId The new art type ID
   */
  updateArtTypeEverywhere(artTypeId: string) {
    this.primaryArtType.value = artTypeId;
    this.selectedArtTypeId = artTypeId;
    this.selectedSeriesArtTypeId = artTypeId;
    this.selectedMaterialArtTypeId = artTypeId;
    this.selectedFeatureArtTypeId = artTypeId;
    this.selectedSeries.ArtTypeId = artTypeId;
    this.selectedClays.ArtTypeId = artTypeId;
    this.selectedGlaze.ArtTypeId = artTypeId;
  }

  /**
   * Handles art type changes and refreshes all related data
   * @param artTypeId The new art type ID
   */
  onArtTypeChange(artTypeId: string) {
    if (artTypeId) {
      this.updateArtTypeEverywhere(artTypeId);
      this.refreshAllArtData();
    } else {
      this.selectedArtTypeId = '';
    }
  }

  /**
   * Handles series art type changes
   * @param artTypeId The new art type ID for series
   */
  onSeriesArtTypeChange(artTypeId: string) {
    console.log('onSeriesArtTypeChange:', artTypeId);
    this.selectedSeriesArtTypeId = artTypeId;
    this.selectedSeries.ArtTypeId = artTypeId;
    this.getAllSeries(artTypeId);
  }

  /**
   * Handles material art type changes
   * @param artTypeId The new art type ID for materials
   */
  onMaterialArtTypeChange(artTypeId: string) {
    console.log('onMaterialArtTypeChange:', artTypeId);
    this.selectedMaterialArtTypeId = artTypeId;
    this.selectedClays.ArtTypeId = artTypeId;
    this.getAllClays(artTypeId);
  }

  /**
   * Handles feature art type changes
   * @param artTypeId The new art type ID for features
   */
  onFeatureArtTypeChange(artTypeId: string) {
    console.log('onFeatureArtTypeChange:', artTypeId);
    this.selectedFeatureArtTypeId = artTypeId;
    this.selectedGlaze.ArtTypeId = artTypeId;
    this.getAllGlazes(artTypeId);
  }


  /**
   * Gets the user's primary art type
   */
  private getPrimaryArtType() {
    this.artTypeService.getPrimaryArtType().subscribe(
      (response: SelectItem) => {
        this.primaryArtType = response || { label: '', value: '' };
        this.updateArtTypeEverywhere(this.primaryArtType.value);
        this.getArtistArtTypes();
        this.getArtTypesOnce();
        this.refreshAllArtData();
      },
      (error) => {
        console.error('Error fetching primary art type:', error);
      }
    );
  }

  /**
   * Loads all art types (only once)
   */
  private getArtTypesOnce() {
    if (this.artTypesLoaded) return;
    this.artTypeService.fetchArttype().subscribe(
      (artTypes: SetupItems[]) => {
        this.artTypes = artTypes;
        // Ensure primaryArtType is always in the dropdown
        if (this.primaryArtType && this.primaryArtType.value && !this.artTypes.some(a => a.value === this.primaryArtType.value)) {
          this.artTypes.push({ label: this.primaryArtType.label, value: this.primaryArtType.value } as any);
        }
        this.artTypesLoaded = true;
      },
      (error) => {
        console.error('Error fetching art types:', error);
        this.artTypesLoaded = false;
      }
    );
  }

  /**
   * Saves the selected art types
   */
  saveArtType() {
    this.ngxSpinnerService.show();
    const uniqueArtTypeIds = Array.from(new Set(this.artistArtTypeId));
    this.artistService.saveArtistArtTypes(uniqueArtTypeIds).subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        this.getArtistArtTypes();
        this.refreshArtistArtTypes(); // This will update all related data

        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.success'), this.transloco.translate('artistOnboardingChecklist.artworkTypesSaved'), NotificationTypes.Success);
        this.advance();
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.failedSaveArtworkTypes'), NotificationTypes.Error);
      }
    );
  }

  /**
   * Gets the artist's selected art types
   */
  private getArtistArtTypes() {
    this.artistService.getArtistArtTypeDropdownItems(this.artistId).subscribe(
      (artistArtTypeDropdownItems: SelectItem[]) => {
        this.selectedArtTypesDropdownItems = artistArtTypeDropdownItems;
        this.artistArtTypeId = artistArtTypeDropdownItems.map(p => p.value);
        // Ensure primaryArtType is always in the dropdown
        if (this.primaryArtType && !this.selectedArtTypesDropdownItems.some(item => item.value === this.primaryArtType.value)) {
          this.selectedArtTypesDropdownItems.push(this.primaryArtType);
        }
        // Update all art type related state at once
        this.updateArtTypeEverywhere(this.primaryArtType.value);
        this.refreshAllArtData();
      },
      (error) => {
        // handle error
      }
    );
  }

  /**
   * Saves the primary art type
   */
  savePrimaryArtType() {
    this.ngxSpinnerService.show();
    this.artistService.savePrimaryArtTypesId(this.primaryArtType.value).subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        this.getArtistArtTypes();
        this.refreshArtistArtTypes(); // This will update all related data

        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.success'), this.transloco.translate('artistOnboardingChecklist.primaryArtTypeSaved'), NotificationTypes.Success);
        this.advance();
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.failedSavePrimaryArtType'), NotificationTypes.Error);
      }
    );
  }

  /**
   * Handles the upgrade dialog close event
   * @param event The close event
   */
  handleDialogClose(event: any) {
    this.upgradeDialogVisible = false;
  }

  /**
   * Gets all series for the specified art type
   * @param artTypeId The art type ID to filter by
   */
  public getAllSeries(artTypeId: string | null = null) {
    console.log('getAllSeries artTypeId:', artTypeId);
    this.ngxSpinnerService.show();
    this.seriesService.getAllSeries(artTypeId).subscribe((series) => {
      this.series = series;
      this.ngxSpinnerService.hide();
    }, () => {
      this.ngxSpinnerService.hide();
    });
  }

  /**
   * Saves the current series
   */

  public saveSeries() {
    console.log('saveSeries called', this.selectedSeries);
    this.ngxSpinnerService.show();
    this.selectedSeries.ArtTypeId =
      this.selectedSeries.ArtTypeId ||
      this.selectedSeriesArtTypeId ||
      this.selectedArtTypeId ||
      this.primaryArtType.value;
    console.log('saveSeries ArtTypeId:', this.selectedSeries.ArtTypeId);

    if (!this.selectedSeries.ArtTypeId) {
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.selectArtworkTypeBeforeSeries'), NotificationTypes.Error);
      return;
    }

    this.seriesService.saveSeries(this.selectedSeries).subscribe(
      (response) => {
        console.log('saveSeries response', response);
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.getAllSeries(this.selectedSeries.ArtTypeId);
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.success'), this.transloco.translate('artistOnboardingChecklist.dataSaved'), NotificationTypes.Success);
          this.selectedSeries.Name = '';
          this.selectedSeries.Description = '';
        } else {
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error('saveSeries error', error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.apiErrorSavingSeries'), NotificationTypes.Error);
      }
    );
  }


  /**
   * Gets all clays for the specified art type
   * @param artTypeId The art type ID to filter by
   */
  private getAllClays(artTypeId: string | null = null) {
    console.log('getAllClays artTypeId:', artTypeId);
    this.ngxSpinnerService.show();
    this.clayService.getAllClays(artTypeId).subscribe((clays) => {
      this.clay = clays;
      this.ngxSpinnerService.hide();
    }, () => {
      this.ngxSpinnerService.hide();
    });
  }


  /**
   * Saves the current marketing features
   */


  public saveFeatures() {
    console.log('saveFeatures called', this.selectedGlaze);
    this.ngxSpinnerService.show();
    this.selectedGlaze.ArtTypeId =
      this.selectedGlaze.ArtTypeId ||
      this.selectedFeatureArtTypeId ||
      this.selectedArtTypeId ||
      this.primaryArtType.value;
    console.log('saveFeatures ArtTypeId:', this.selectedGlaze.ArtTypeId);

    if (!this.selectedGlaze.ArtTypeId) {
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.selectArtworkTypeBeforeFeatures'), NotificationTypes.Error);
      return;
    }

    this.glazesService.saveGlazes(this.selectedGlaze).subscribe(
      (response) => {
        console.log('saveFeatures response', response);
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.getAllGlazes(this.selectedGlaze.ArtTypeId);
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.success'), this.transloco.translate('artistOnboardingChecklist.dataSaved'), NotificationTypes.Success);
          this.selectedGlaze.Name = '';
          this.selectedGlaze.Description = '';
        } else {
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error('saveFeatures error', error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.apiErrorSavingFeatures'), NotificationTypes.Error);
      }
    );
  }

  /**
   * Saves the current material
   */


  public saveMaterial() {
    console.log('saveMaterial called', this.selectedClays);
    this.ngxSpinnerService.show();
    this.selectedClays.ArtTypeId =
      this.selectedClays.ArtTypeId ||
      this.selectedMaterialArtTypeId ||
      this.selectedArtTypeId ||
      this.primaryArtType.value;
    console.log('saveMaterial ArtTypeId:', this.selectedClays.ArtTypeId);

    if (!this.selectedClays.ArtTypeId) {
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.selectArtworkTypeBeforeMaterial'), NotificationTypes.Error);
      return;
    }

    this.clayService.saveMaterial(this.selectedClays).subscribe(
      (response) => {
        console.log('saveMaterial response', response);
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.getAllClays(this.selectedClays.ArtTypeId);
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.success'), this.transloco.translate('artistOnboardingChecklist.dataSaved'), NotificationTypes.Success);
          this.selectedClays.Name = '';
          this.selectedClays.Description = '';
        } else {
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error('saveMaterial error', error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.apiErrorSavingMaterial'), NotificationTypes.Error);
      }
    );
  }


  /**
   * Gets all glazes for the specified art type
   * @param artTypeId The art type ID to filter by
   */
  private getAllGlazes(artTypeId: string | null = null) {
    console.log('getAllGlazes artTypeId:', artTypeId);
    this.ngxSpinnerService.show();
    this.glazesService.getAllGlazes(artTypeId).subscribe((glazes) => {
      this.glazes = glazes;
      this.ngxSpinnerService.hide();
    }, () => {
      this.ngxSpinnerService.hide();
    });
  }


  /**
   * Saves updated user details
   * @param updatedUser The updated user data
   */
  saveUpdatedUser(updatedUser: any) {
    this.updatedUser = updatedUser;
    this.advance();
  }

  /**
   * Saves updated user resume and statement
   * @param updatedUser The updated user data
   */
  saveUpdatedUserResume(updatedUser: any) {
    this.updatedUser = updatedUser;
    this.advance();
  }

  /**
   * Saves the artist's first artwork
   * @param createproduct The artwork data to save
   */
  saveArtistProduct(createproduct: any) {
    this.createproduct = createproduct;
    this.advance();
  }

  /**
   * Skips the onboarding checklist
   */
  public onSkipChecklist(): void {
    this.ngxSpinnerService.show();
    this.UsersService.SkipOnboardingChecklist().subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.router.navigate(['/dashboard']);
        } else {
          this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      () => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('artistOnboardingChecklist.error'), this.transloco.translate('artistOnboardingChecklist.errorSkippingChecklist'), NotificationTypes.Error);
      }
    );
  }

  /**
   * Refreshes all art-related data for the specified art type
   * @param artTypeId The art type ID to refresh data for
   */
  public refreshAllArtData() {
    this.getAllSeries();
    this.getAllClays();
    this.getAllGlazes();
  }

  /**
   * Gets alternative art types (excluding the primary one)
   * @returns Array of alternative art types
   */
  getAlternativeArtTypes() {
    return this.artTypes?.filter(a => a.Id !== this.primaryArtType.value) || [];
  }

  /**
   * Refreshes all artist art type related data
   */
  refreshArtistArtTypes() {
    this.getArtistArtTypes();
    this.getArtTypesOnce();
    this.refreshAllArtData();
    this.getPrimaryArtType();
  }
}